import {
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  deleteFamilyMemberAttributes,
  getMedsiQuestionnaire,
  getMemberDetails,
  getQuestionnaireOptionSearchV3,
  getQuestionnaireV3,
  postFamilyMemberAttributes,
  postFeedback,
  postMedsiQuestionnaire,
  postQuestionnaireOptionSearchV3,
  postQuestionnaireV3,
  postUserAttributes,
  updateFamilyAttributes,
  updateMedsiQuestionnaire,
  updateQuestionnaireV3,
  updateUserAttributes,
} from './api';
import {
  FeedbackPayload,
  MemberDetail,
  QuestionnairePayload,
  QuestionnaireResponse,
  QuestionnaireV3Payload,
  SearchChoice,
  UserAttributeData,
} from './type';
import { QuestionnaireItem } from 'types';

interface GetMemberDetailsArgs extends UseQueryOptions {
  profileId?: string;
}
export const useGetMemberDetails = (
  args?: Omit<GetMemberDetailsArgs, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: ['memeber-details'],
    queryFn: getMemberDetails,
    ...args,
  }) as UseQueryResult<MemberDetail[]>;
};

export const usePostFamilyMemberAttributes = (
  args?: Omit<
    UseMutationOptions<
      { profile_id: string },
      Error,
      UserAttributeData,
      unknown
    >,
    'mutationFn'
  >
) => {
  return useMutation({
    mutationFn: postFamilyMemberAttributes,
    ...args,
  });
};

export const useGetMedsiQuestionnaire = (params?: {
  user_history?: boolean;
  profile_id: string;
}) => {
  return useQuery({
    queryKey: ['medsi-questionnaire', params?.profile_id, params?.user_history],
    queryFn: () => getMedsiQuestionnaire(params),
  });
};

export const usePostMedsiQuestionnaire = (
  args?: Omit<
    UseMutationOptions<unknown, Error, QuestionnairePayload[], unknown>,
    'mutationFn'
  >
) => {
  return useMutation({
    mutationFn: postMedsiQuestionnaire,
    ...args,
  });
};

export const useUpdateMedsiQuestionnaire = (
  args?: Omit<
    UseMutationOptions<unknown, Error, QuestionnairePayload[], unknown>,
    'mutationFn'
  >
) => {
  return useMutation({
    mutationFn: updateMedsiQuestionnaire,
    ...args,
  });
};

export const usePostFeedback = (
  args?: Omit<
    UseMutationOptions<unknown, Error, FeedbackPayload, unknown>,
    'mutationFn'
  >
) => {
  return useMutation({
    mutationFn: postFeedback,
    ...args,
  });
};

export const usePostUserAttributes = (
  args?: Omit<
    UseMutationOptions<unknown, Error, unknown, unknown>,
    'mutationFn'
  >
) => {
  return useMutation({
    mutationFn: postUserAttributes,
    ...args,
  });
};

export const useUpdateUserAttributes = (
  args?: Omit<UseMutationOptions<unknown, Error, unknown>, 'mutationFn'>
) => {
  return useMutation({
    mutationFn: updateUserAttributes,
    ...args,
  });
};

export const useUpdateFamilyAttributes = (
  args?: Omit<UseMutationOptions<unknown, Error, unknown>, 'mutationFn'>
) => {
  return useMutation({
    mutationFn: updateFamilyAttributes,
    ...args,
  });
};

export const useDeleteFamilyAttributes = (
  args?: Omit<UseMutationOptions<unknown, Error, unknown>, 'mutationFn'>
) => {
  return useMutation({
    mutationFn: deleteFamilyMemberAttributes,
    ...args,
  });
};

interface GetQuestionnaireV3Args extends UseQueryOptions {
  payload: QuestionnaireV3Payload & { profileId: string };
}
export const useGetQuestionnaireV3 = (
  args: Omit<GetQuestionnaireV3Args, 'queryKey' | 'queryFn'>
) => {
  const type =
    'retrieve_type' in args.payload
      ? args.payload.retrieve_type
      : args.payload.type;

  return useQuery({
    queryKey: ['questionnaire-v3', type, args.payload.profileId],
    queryFn: () => getQuestionnaireV3(args.payload),
    staleTime: 0,
    gcTime: 0,
    ...args,
  }) as UseQueryResult<QuestionnaireResponse>;
};

export const usePostQuestionnaireV3 = (
  args?: Omit<
    UseMutationOptions<
      { data: [QuestionnaireItem] },
      Error,
      QuestionnairePayload,
      unknown
    >,
    'mutationFn'
  >
) => {
  return useMutation({
    mutationFn: postQuestionnaireV3,
    ...args,
  });
};

export const useUpdateQuestionnaireV3 = (
  args?: Omit<
    UseMutationOptions<
      { has_follow_up: boolean; data: [QuestionnaireItem] | null },
      Error,
      QuestionnairePayload,
      unknown
    >,
    'mutationFn'
  >
) => {
  return useMutation({
    mutationFn: updateQuestionnaireV3,
    ...args,
  });
};

export const useGetQuestionnaireOptionSearchV3 = (paylaod: {
  code: string;
  search_text: string;
}) => {
  return useQuery({
    queryKey: ['questionnaire-option-search-v3', paylaod.code],
    queryFn: async () => await getQuestionnaireOptionSearchV3(paylaod),
    enabled: !!paylaod.code,
  }) as UseQueryResult<{
    eng_choices: SearchChoice[];
    spanish_choices: SearchChoice[];
  }>;
};

export const usePostQuestionnaireOptionSearchV3 = (
  args?: Omit<
    UseMutationOptions<unknown, Error, { combos: string[] }, unknown>,
    'mutationFn'
  >
) => {
  return useMutation({
    mutationFn: postQuestionnaireOptionSearchV3,
    ...args,
  });
};
